/**
 * User Management Page (Admin Only)
 * Allows administrators to create, view, and manage user accounts
 * Part of the invite-only registration system
 */
import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../api';
import { toast } from 'sonner';
import {
  Users, UserPlus, Trash2, Key, Shield, Mail, Clock, CheckCircle,
  XCircle, RefreshCw, Eye, EyeOff, AlertTriangle, Send, Lock, MoreVertical,
  Filter, Search, X
} from 'lucide-react';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showForcePasswordModal, setShowForcePasswordModal] = useState(null);
  const [showSendCredentialsModal, setShowSendCredentialsModal] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which user action is loading
  const [createLoading, setCreateLoading] = useState(false);
  const [welcome, setWelcome] = useState({ send: true, withPassword: true });
  const [showPassword, setShowPassword] = useState(false);
  const [forcePasswordReason, setForcePasswordReason] = useState('');
  const [sendEmailWithForce, setSendEmailWithForce] = useState(true);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState('all'); // Role filter state
  const [statusFilter, setStatusFilter] = useState('all'); // Status filter state
  const [searchQuery, setSearchQuery] = useState(''); // Search query state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'Clinician',
    agency: 'Ivory Health'
  });
  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  // Get unique roles from users for filter dropdown
  const uniqueRoles = [...new Set(users.map(u => u.role))].sort();

  // Filter users based on role, status, and search query
  const filteredUsers = users.filter(user => {
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && !user.lockedUntil) ||
      (statusFilter === 'locked' && user.lockedUntil) ||
      (statusFilter === 'mustChange' && user.mustChangePassword);
    const matchesSearch = searchQuery === '' || 
      `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesRole && matchesStatus && matchesSearch;
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const response = await api.get('/auth/admin/users');
      if (response.data.success) {
        setUsers(response.data.users);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Admin privileges required.');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setPasswordErrors([]);
    setCreateLoading(true);

    try {
      const response = await api.post('/auth/admin/create-user', newUser);
      if (response.data.success) {
        toast.success(`User ${newUser.email} created successfully`);
        if (welcome.send) {
          api.post(`/caseload/welcome/${response.data.user.id}`, { tempPassword: welcome.withPassword ? newUser.password : null })
            .then(r => toast.success(r.data.message)).catch(e => toast.error(e.response?.data?.detail || 'Welcome email failed'));
        }
        setShowCreateForm(false);
        setNewUser({
          email: '',
          password: '',
          firstName: '',
          lastName: '',
          role: 'Clinician',
          agency: 'Ivory Health'
        });
        loadUsers();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors) {
        setPasswordErrors(detail.errors);
      }
      toast.error(detail?.message || detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleDeleteUser = async (userId, userEmail) => {
    if (!window.confirm(`Are you sure you want to delete ${userEmail}? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await api.delete(`/auth/admin/users/${userId}`);
      if (response.data.success) {
        toast.success('User deleted successfully');
        loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!resetPasswordValue) {
      toast.error('Please enter a new password');
      return;
    }

    try {
      const response = await api.put(`/auth/admin/users/${userId}/reset-password`, {
        newPassword: resetPasswordValue
      });
      if (response.data.success) {
        toast.success('Password reset successfully');
        setShowResetPassword(null);
        setResetPasswordValue('');
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail?.errors) {
        toast.error(detail.errors.join(', '));
      } else {
        toast.error(detail?.message || detail || 'Failed to reset password');
      }
    }
  };

  // Force Password Change - User must change on next login
  const handleForcePasswordChange = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await api.put(`/auth/admin/users/${userId}/force-password-change`, {
        reason: forcePasswordReason,
        sendEmail: sendEmailWithForce
      });
      if (response.data.success) {
        toast.success(response.data.message || 'Password change required on next login');
        if (response.data.emailSent?.success) {
          toast.success('Notification email sent to user');
        } else if (sendEmailWithForce && response.data.emailSent?.error) {
          toast.warning('User notified, but email delivery may be pending (domain verification needed)');
        }
        setShowForcePasswordModal(null);
        setForcePasswordReason('');
        loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to force password change');
    } finally {
      setActionLoading(null);
    }
  };

  // Send/Resend Credentials - Generate temp password and email to user
  const handleSendCredentials = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await api.post(`/auth/admin/users/${userId}/send-credentials`, {});
      if (response.data.success) {
        toast.success(response.data.message || 'Credentials sent successfully');
        if (response.data.emailResult?.success) {
          toast.success('Email delivered successfully');
        } else if (response.data.emailResult?.error) {
          toast.warning('Credentials generated, but email delivery may be pending (domain verification needed)');
        }
        setShowSendCredentialsModal(null);
        loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk Force Password Change
  const handleBulkForcePasswordChange = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    setActionLoading('bulk');
    try {
      const response = await api.post('/auth/admin/users/bulk-force-password-change', {
        userIds: selectedUsers,
        reason: forcePasswordReason,
        sendEmails: sendEmailWithForce
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedUsers([]);
        setForcePasswordReason('');
        loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update users');
    } finally {
      setActionLoading(null);
    }
  };

  // Bulk Send Credentials
  const handleBulkSendCredentials = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user');
      return;
    }
    setActionLoading('bulk');
    try {
      const response = await api.post('/auth/admin/users/bulk-send-credentials', {
        userIds: selectedUsers
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setSelectedUsers([]);
        loadUsers();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send credentials');
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select/Deselect all users (filtered)
  const toggleSelectAll = () => {
    const filteredIds = filteredUsers.map(u => u.id);
    const allFilteredSelected = filteredIds.every(id => selectedUsers.includes(id));
    if (allFilteredSelected) {
      // Deselect all filtered users
      setSelectedUsers(prev => prev.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered users
      setSelectedUsers(prev => [...new Set([...prev, ...filteredIds])]);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setRoleFilter('all');
    setStatusFilter('all');
    setSearchQuery('');
  };

  const hasActiveFilters = roleFilter !== 'all' || statusFilter !== 'all' || searchQuery !== '';

  const roleColors = {
    'Admin': 'bg-red-100 text-red-700',
    'Clinician': 'bg-blue-100 text-blue-700',
    'Billing': 'bg-green-100 text-green-700',
    'Office Staff': 'bg-purple-100 text-purple-700',
    'Read Only': 'bg-gray-100 text-gray-700',
    'User': 'bg-gray-100 text-gray-700'
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%^&*';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewUser(prev => ({ ...prev, password }));
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="w-7 h-7 text-blue-600" />
              User Management
            </h1>
            <p className="text-gray-500 mt-1">Create and manage user accounts (Admin Only)</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={loadUsers} variant="outline" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button 
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-user-btn"
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Create User
            </Button>
          </div>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <Shield className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-blue-800">Invite-Only System</p>
            <p className="text-sm text-blue-600">
              Public registration is disabled. Only administrators can create new user accounts. 
              New users will be required to change their password on first login and acknowledge HIPAA compliance.
            </p>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="bg-white rounded-lg border p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="user-search-input"
              />
            </div>

            {/* Role Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm min-w-[150px]"
                data-testid="role-filter"
              >
                <option value="all">All Roles</option>
                {uniqueRoles.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm min-w-[150px]"
              data-testid="status-filter"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="locked">Locked</option>
              <option value="mustChange">Must Change Password</option>
            </select>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="text-gray-600"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4 mr-1" />
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Results Summary */}
          <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing <strong>{filteredUsers.length}</strong> of <strong>{users.length}</strong> users
              {hasActiveFilters && (
                <span className="ml-2 text-blue-600">
                  (filtered)
                </span>
              )}
            </span>
            {hasActiveFilters && (
              <div className="flex items-center gap-2">
                {roleFilter !== 'all' && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                    Role: {roleFilter}
                  </span>
                )}
                {statusFilter !== 'all' && (
                  <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                    Status: {statusFilter === 'mustChange' ? 'Must Change' : statusFilter}
                  </span>
                )}
                {searchQuery && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                    Search: &ldquo;{searchQuery}&rdquo;
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Create User Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <UserPlus className="w-5 h-5 text-blue-600" />
                  Create New User
                </h2>
              </div>
              <form onSubmit={handleCreateUser} className="p-6 space-y-4">
                {passwordErrors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="font-medium text-red-800 text-sm mb-1">Password Requirements:</p>
                    <ul className="text-sm text-red-600 list-disc list-inside">
                      {passwordErrors.map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name *</Label>
                    <Input
                      value={newUser.firstName}
                      onChange={(e) => setNewUser(prev => ({ ...prev, firstName: e.target.value }))}
                      required
                      className="mt-1"
                      data-testid="create-first-name"
                    />
                  </div>
                  <div>
                    <Label>Last Name *</Label>
                    <Input
                      value={newUser.lastName}
                      onChange={(e) => setNewUser(prev => ({ ...prev, lastName: e.target.value }))}
                      required
                      className="mt-1"
                      data-testid="create-last-name"
                    />
                  </div>
                </div>

                <div>
                  <Label>Email Address *</Label>
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    required
                    className="mt-1"
                    data-testid="create-email"
                  />
                </div>

                <div>
                  <Label>Temporary Password *</Label>
                  <div className="flex gap-2 mt-1">
                    <div className="relative flex-1">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={newUser.password}
                        onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                        required
                        data-testid="create-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <Button type="button" variant="outline" onClick={generatePassword}>
                      Generate
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Min 8 chars, uppercase, lowercase, number, special character
                  </p>
                </div>

                <div>
                  <Label>Role *</Label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser(prev => ({ ...prev, role: e.target.value }))}
                    className="w-full mt-1 px-3 py-2 border rounded-md"
                    data-testid="create-role"
                  >
                    <option value="Admin">Admin</option>
                    <option value="Clinician">Clinician</option>
                    <option value="Billing">Billing</option>
                    <option value="Office Staff">Office Staff</option>
                    <option value="Read Only">Read Only</option>
                  </select>
                </div>

                <div>
                  <Label>Agency</Label>
                  <Input
                    value={JSON.parse(localStorage.getItem('ivory_user') || '{}').agency || newUser.agency}
                    readOnly
                    className="mt-1 bg-gray-100 text-gray-600"
                    data-testid="create-agency"
                  />
                  <p className="text-xs text-gray-500 mt-1">New logins always join your agency.</p>
                </div>
                <div className="space-y-1.5 rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={welcome.send} onChange={e => setWelcome(w => ({ ...w, send: e.target.checked }))} data-testid="create-welcome-email" />
                    Email a welcome with the sign-in link, Nurse Guide PDF and audio</label>
                  {welcome.send && <label className="flex items-center gap-2 text-sm ml-5"><input type="checkbox" checked={welcome.withPassword} onChange={e => setWelcome(w => ({ ...w, withPassword: e.target.checked }))} data-testid="create-welcome-password" />
                    Include the temporary password in the email</label>}
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowCreateForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    data-testid="submit-create-user"
                  >
                    {createLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <UserPlus className="w-4 h-4 mr-2" />
                    )}
                    Create User
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Force Password Change Modal */}
        {showForcePasswordModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="force-password-modal">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Lock className="w-5 h-5 text-orange-600" />
                  Force Password Change
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600">
                  This will require the user to change their password on their next login.
                  The user will be unable to access the system until they set a new password.
                </p>
                
                <div>
                  <Label>Reason (optional)</Label>
                  <Input
                    value={forcePasswordReason}
                    onChange={(e) => setForcePasswordReason(e.target.value)}
                    placeholder="e.g., Security policy update, Suspected compromise"
                    className="mt-1"
                    data-testid="force-password-reason"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendEmailForce"
                    checked={sendEmailWithForce}
                    onChange={(e) => setSendEmailWithForce(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <label htmlFor="sendEmailForce" className="text-sm text-gray-600">
                    Send email notification to user
                  </label>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Note: Email delivery requires domain verification in Resend dashboard.
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowForcePasswordModal(null);
                      setForcePasswordReason('');
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleForcePasswordChange(showForcePasswordModal)}
                    disabled={actionLoading === showForcePasswordModal}
                    className="flex-1 bg-orange-600 hover:bg-orange-700"
                    data-testid="confirm-force-password"
                  >
                    {actionLoading === showForcePasswordModal ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Lock className="w-4 h-4 mr-2" />
                    )}
                    Force Change
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Send Credentials Modal */}
        {showSendCredentialsModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" data-testid="send-credentials-modal">
              <div className="p-6 border-b">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Send className="w-5 h-5 text-green-600" />
                  Send Login Credentials
                </h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600">
                  This will generate a new temporary password and send it to the user&apos;s email address.
                  The user will be required to change this password on their next login.
                </p>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <Mail className="w-4 h-4 inline mr-1" />
                    A welcome email with login instructions will be sent to the user.
                  </p>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">
                    <AlertTriangle className="w-4 h-4 inline mr-1" />
                    Note: Email delivery requires domain verification in Resend dashboard.
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowSendCredentialsModal(null)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={() => handleSendCredentials(showSendCredentialsModal)}
                    disabled={actionLoading === showSendCredentialsModal}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid="confirm-send-credentials"
                  >
                    {actionLoading === showSendCredentialsModal ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4 mr-2" />
                    )}
                    Send Credentials
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {selectedUsers.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center justify-between">
            <span className="font-medium text-blue-800">
              {selectedUsers.length} user(s) selected
            </span>
            <div className="flex gap-2">
              <Button
                onClick={handleBulkForcePasswordChange}
                disabled={actionLoading === 'bulk'}
                variant="outline"
                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                data-testid="bulk-force-password-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Force Password Change
              </Button>
              <Button
                onClick={handleBulkSendCredentials}
                disabled={actionLoading === 'bulk'}
                variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="bulk-send-credentials-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Send Credentials
              </Button>
              <Button
                onClick={() => setSelectedUsers([])}
                variant="outline"
              >
                Clear Selection
              </Button>
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full" data-testid="users-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left">
                  <input
                    type="checkbox"
                    checked={filteredUsers.length > 0 && filteredUsers.every(u => selectedUsers.includes(u.id))}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Password Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    {hasActiveFilters ? (
                      <div>
                        <p>No users match your filters</p>
                        <Button variant="link" onClick={clearFilters} className="mt-2">
                          Clear all filters
                        </Button>
                      </div>
                    ) : (
                      'No users found'
                    )}
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id} className={`hover:bg-gray-50 ${selectedUsers.includes(user.id) ? 'bg-blue-50' : ''}`}>
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => toggleUserSelection(user.id)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {user.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${roleColors[user.role] || roleColors['User']}`}>
                        {user.role}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {user.lockedUntil ? (
                        <span className="flex items-center gap-1 text-red-600 text-sm">
                          <XCircle className="w-4 h-4" />
                          Locked
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          Active
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {user.mustChangePassword ? (
                        <span className="flex items-center gap-1 text-orange-600 text-sm">
                          <AlertTriangle className="w-4 h-4" />
                          Must Change
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="w-4 h-4" />
                          OK
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {user.lastLoginAt ? (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(user.lastLoginAt).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="text-gray-400">Never</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {/* Force Password Reset Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowForcePasswordModal(user.id)}
                          title="Force Password Change"
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          disabled={actionLoading === user.id}
                          data-testid={`force-password-btn-${user.id}`}
                        >
                          <Lock className="w-4 h-4" />
                        </Button>
                        
                        {/* Send/Resend Credentials Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowSendCredentialsModal(user.id)}
                          title="Send/Resend Credentials"
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          disabled={actionLoading === user.id}
                          data-testid={`send-credentials-btn-${user.id}`}
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                        
                        {/* Manual Reset Password Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowResetPassword(user.id)}
                          title="Manually Reset Password"
                          data-testid={`reset-password-btn-${user.id}`}
                        >
                          <Key className="w-4 h-4" />
                        </Button>
                        
                        {/* Delete User Button */}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteUser(user.id, user.email)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete User"
                          data-testid={`delete-user-btn-${user.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>

                      {/* Reset Password Popover */}
                      {showResetPassword === user.id && (
                        <div className="absolute mt-2 p-3 bg-white border rounded-lg shadow-lg z-10">
                          <Label className="text-xs">New Password</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="text"
                              value={resetPasswordValue}
                              onChange={(e) => setResetPasswordValue(e.target.value)}
                              placeholder="Enter new password"
                              className="text-sm"
                            />
                            <Button
                              size="sm"
                              onClick={() => handleResetPassword(user.id)}
                              className="bg-blue-600"
                            >
                              Reset
                            </Button>
                          </div>
                          <button
                            onClick={() => {
                              setShowResetPassword(null);
                              setResetPasswordValue('');
                            }}
                            className="text-xs text-gray-500 mt-2 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Warning Notice */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-yellow-800">Security Notice</p>
            <p className="text-sm text-yellow-600">
              All user management actions are logged in the HIPAA audit trail. 
              Share temporary passwords securely and ensure users change them immediately.
            </p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default UserManagementPage;
